import { Modal, toast } from "@devhop/ui";
import { useCallback, useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type {
  CategoryCreateInput,
  CategoryUpdateInput,
} from "@/modules/categories/domain/contracts";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import type { CategoryDTO } from "../api/client";
import { categoriesApi } from "../api/client";
import { useCategoriesQuery, useDeleteCategory } from "../api/queries";
import { CategoriesFilter } from "../ui/CategoriesFilter";
import { CategoriesToolbar } from "../ui/CategoriesToolbar";
import { CategoriesTree } from "../ui/CategoriesTree";
import { CategoryForm } from "../ui/CategoryForm";

const TREE_QUERY = { limit: 1000, offset: 0 } as const;

export function CategoriesPage() {
  const list = useCategoriesQuery(TREE_QUERY);
  const deleteCategory = useDeleteCategory();
  const canCreate = useActionPermission(["categories:create"]);
  const canManage = useActionPermission(
    ["categories:update", "categories:delete"],
    "any",
  );

  const [submitting, setSubmitting] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CategoryDTO | null>(
    null,
  );
  const [defaultParentId, setDefaultParentId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const handleSearchChange = useCallback((q: string) => {
    setSearchQuery(q);
  }, []);

  const data = list.data?.data ?? [];
  const isEdit = editingCategory != null;

  const formParentId = isEdit
    ? (editingCategory?.parentId ?? null)
    : defaultParentId;
  const formParentName = useMemo(() => {
    if (!formParentId) return null;
    return data.find((c) => c.id === formParentId)?.name ?? null;
  }, [data, formParentId]);

  const openCreateRoot = () => {
    setEditingCategory(null);
    setDefaultParentId(null);
    setFormOpen(true);
  };

  const openCreateChild = (parent: CategoryDTO) => {
    setEditingCategory(null);
    setDefaultParentId(parent.id);
    setFormOpen(true);
  };

  const openEdit = (row: CategoryDTO) => {
    setEditingCategory(row);
    setDefaultParentId(null);
    setFormOpen(true);
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຫມວດຫມູ່" },
          ]}
        />
      </Header>

      <Main>
        <CategoriesToolbar
          onCreate={openCreateRoot}
          canCreate={!!canCreate}
        />

        <Modal
          open={formOpen}
          onOpenChange={(open) => {
            setFormOpen(open);
            if (!open) {
              setEditingCategory(null);
              setDefaultParentId(null);
            }
          }}
          title={
            isEdit
              ? "ແກ້ໄຂຫມວດຫມູ່"
              : defaultParentId
                ? "ເພີ່ມຫມວດຍ່ອຍ"
                : "ເພີ່ມຫມວດຫຼັກ"
          }
          description={
            isEdit
              ? "ແກ້ໄຂຊື່ຫມວດຫມູ່"
              : formParentId
                ? "ກຳນົດຊື່ຫມວດຍ່ອຍ"
                : "ກຳນົດຊື່ຫມວດຫຼັກ"
          }
          size="md"
        >
          <CategoryForm
            key={
              editingCategory?.id ??
              defaultParentId ??
              "new-root"
            }
            initialValues={
              editingCategory
                ? { name: editingCategory.name }
                : { name: "" }
            }
            parentId={formParentId}
            parentName={formParentName}
            onSubmit={async (vals) => {
              try {
                setSubmitting(true);
                if (isEdit && editingCategory) {
                  await categoriesApi.update(
                    editingCategory.id,
                    vals as CategoryUpdateInput,
                  );
                  toast.success("ແກ້ໄຂຫມວດຫມູ່ສຳເລັດ");
                } else {
                  await categoriesApi.create(vals as CategoryCreateInput);
                  toast.success("ເພີ່ມຫມວດຫມູ່ສຳເລັດ");
                }
                setFormOpen(false);
                setEditingCategory(null);
                setDefaultParentId(null);
                await list.refetch();
              } catch {
                toast.error(isEdit ? "ແກ້ໄຂລົ້ມເຫຼວ" : "ເພີ່ມລົ້ມເຫຼວ");
              } finally {
                setSubmitting(false);
              }
            }}
            onCancel={() => setFormOpen(false)}
            submitting={submitting}
          />
        </Modal>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <CategoriesFilter onSearchChange={handleSearchChange} />
          <CategoriesTree
            canManage={!!canManage}
            canCreate={!!canCreate}
            isLoading={list.isLoading}
            data={data}
            searchQuery={searchQuery}
            onEdit={openEdit}
            onAddChild={openCreateChild}
            onCreateRoot={openCreateRoot}
            onDelete={async (id: string) => {
              toast.promise(deleteCategory.run(id), {
                loading: "ກຳລັງລຶບ...",
                success: "ລຶບຫມວດຫມູ່ສຳເລັດ",
                error: "ລຶບລົ້ມເຫຼວ",
              });
            }}
          />
        </div>
      </Main>
    </>
  );
}
